import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiBookOpen, FiVideo, FiUser, FiFileText, FiTarget, FiPlay, FiUsers } from 'react-icons/fi';

interface HeroProps {
  onOurPromiseClick: () => void;
  onSuccessStoriesClick: () => void;
  onBooksClick: () => void;
  onVideoLearningClick: () => void;
  onBlogsClick: () => void;
  onMkAcademyOnlineClick?: () => void;
}

const HeroSection = styled.section`
  min-height: 100vh;
  background: linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #3b82f6 100%);
  color: white;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  padding-top: 80px;
`;

const HeroBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="heroPattern" width="50" height="50" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1" fill="white" opacity="0.1"/><circle cx="10" cy="10" r="0.5" fill="white" opacity="0.05"/><circle cx="40" cy="40" r="0.5" fill="white" opacity="0.05"/></pattern></defs><rect width="100" height="100" fill="url(%23heroPattern)"/></svg>');
  opacity: 0.3;
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    text-align: center;
    gap: 2rem;
  }
`;

const LeftSection = styled.div`
  position: relative;
  
  h1 {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 1.5rem;
    line-height: 1.2;
    background: rgba(255, 255, 255, 0.1);
    padding: 1.5rem;
    border-radius: 15px;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    position: relative;
    z-index: 2;

    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }

  p {
    font-size: 1.1rem;
    margin-bottom: 2rem;
    opacity: 0.9;
    line-height: 1.6;
  }
`;

const RightSection = styled.div`
  h2 {
    font-size: 2.2rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
    line-height: 1.2;

    @media (max-width: 768px) {
      font-size: 1.8rem;
    }
  }

  .subtitle {
    font-size: 1.1rem;
    margin-bottom: 2rem;
    opacity: 0.9;
  }
`;

const LearningGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.75rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const LearningCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  padding: 1rem;
  border-radius: 10px;
  text-align: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-5px);
    background: rgba(255, 255, 255, 0.25);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(-2px);
  }

  .icon {
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
    display: block;
  }

  .label {
    font-size: 0.8rem;
    font-weight: 500;
    opacity: 0.9;
  }
`;

const CTAButton = styled.button`
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: #1e3a8a;
  border: none;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 25px rgba(251, 191, 36, 0.3);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 35px rgba(251, 191, 36, 0.4);
  }
`;

const StatsSection = styled.div`
  display: flex;
  gap: 2rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    justify-content: center;
    flex-wrap: wrap;
  }
`;

const StatItem = styled.div`
  text-align: center;

  .number {
    font-size: 2rem;
    font-weight: 700;
    display: block;
    color: #fbbf24;
  }

  .label {
    font-size: 0.9rem;
    opacity: 0.8;
  }
`;

const ChatBubble = styled.div`
  position: absolute;
  top: -20px;
  right: -20px;
  background: #fbbf24;
  color: #1e3a8a;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  animation: bounce 2s infinite;

  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(-10px);
    }
    60% {
      transform: translateY(-5px);
    }
  }
`;

const Hero: React.FC<HeroProps> = ({ 
  onOurPromiseClick, 
  onSuccessStoriesClick, 
  onBooksClick, 
  onVideoLearningClick, 
  onBlogsClick, 
  onMkAcademyOnlineClick 
}) => {
  return (
    <HeroSection>
      <HeroBackground />
      <HeroContent>
        <LeftSection>
          <h1>
            Build Knowledge with Confidence
          </h1>
          <p>
            Transform your learning journey with our all-in-one platform.
            We bring together trusted books and dynamic video lessons to create a complete experience.
            Learning becomes accessible with clear guidance and easy-to-follow resources.
            It's enjoyable through engaging formats that keep learners motivated.
            And most importantly, it's effective—designed to support growth at every level.
          </p>
          <div>
            <CTAButton onClick={onOurPromiseClick}>
              Start Learning Today
            </CTAButton>
          </div>
        </LeftSection>

        <RightSection>
          <h2>
            Why Choose MathKnots?
          </h2>
          <p className="subtitle">
            Our proven approach combines multiple learning methods for maximum effectiveness
          </p>

          <div>
            <LearningGrid>
              <LearningCard onClick={onBooksClick}>
                <span className="icon">📚</span>
                <div className="label">Books</div>
              </LearningCard>
              <LearningCard onClick={onVideoLearningClick}>
                <span className="icon">🎥</span>
                <div className="label">Videos</div>
              </LearningCard>
              <LearningCard onClick={onMkAcademyOnlineClick || (() => console.log('MK Academy navigation not available'))}>
                <span className="icon">🏫</span>
                <div className="label">MK Academy</div>
              </LearningCard>
              <LearningCard onClick={onBlogsClick}>
                <span className="icon">📝</span>
                <div className="label">Blogs</div>
              </LearningCard>
            </LearningGrid>
          </div>

          <div>
            <StatsSection>
              <StatItem>
                <span className="number">10K+</span>
                <span className="label">Students</span>
              </StatItem>
              <StatItem>
                <span className="number">500+</span>
                <span className="label">Lessons</span>
              </StatItem>
              <StatItem>
                <span className="number">95%</span>
                <span className="label">Success Rate</span>
              </StatItem>
            </StatsSection>
          </div>
        </RightSection>
      </HeroContent>
    </HeroSection>
  );
};

export default Hero; 